import { ApolloError, gql, useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Server } from './apollo/types';
import { logger } from './datadog';
import { clientRequiresSafetyGuardsAtom } from './visitor/state';

export type ClientRequiresTelehealthSafetyGuardsQuery = {
  clientRequiresTelehealthSafetyGuards: boolean;
};

export type ClientRequiresTelehealthSafetyGuardsQueryVariables = {
  patientShortId: string;
};

export const GET_CLIENT_SAFETY_GUARD = gql`
  query ClientRequiresTelehealthSafetyGuards($patientShortId: String!) {
    clientRequiresTelehealthSafetyGuards(patientShortId: $patientShortId)
  }
`;

function computeResult(
  data: ClientRequiresTelehealthSafetyGuardsQuery | null | undefined,
  loading: boolean | undefined,
  error: ApolloError | undefined,
) {
  if (loading && !data) {
    return undefined;
  } else if (error) {
    // If the query fails, default to true to err on the side of caution for
    // clients whose safety guard status is unknown.
    return true;
  } else {
    return data?.clientRequiresTelehealthSafetyGuards ?? true;
  }
}

/**
 * Wrapper for useQuery to get the client's safety guard status. Safety guards
 * are legally required for clients under 13 years old for several experiences
 * in Telehealth, such as filling out forms and using chat. Query is public and invocable
 * by either the client (visitor) or the provider. For the provider, it is
 * necessary to pass the patient short ID as a parameter to the hook.
 *
 * @param passedPatientShortId - Optional patient short ID to pass to the hook.
 * Required if used in a provider context.
 * @returns QueryResult and callback function for the result of the query.
 */
export function useGetClientSafetyGuard(passedPatientShortId?: string) {
  const { patientShortId: patientShortIdParam } = useParams();
  const patientShortId = passedPatientShortId ?? patientShortIdParam;
  const apolloClient = useApolloClient();

  const { data, error, loading, ...rest } = useQuery<ClientRequiresTelehealthSafetyGuardsQuery>(
    GET_CLIENT_SAFETY_GUARD,
    {
      variables: { patientShortId },
      context: { server: Server.MONOLITH, skipAuth: true },
      skip: !patientShortId,
    },
  );

  // Memoized callback to read the cached result from the Apollo cache at run
  // time. Returns boolean value or undefined if no data is available in the
  // Apollo cache (i.e. the query has not yet completed/errored out).
  const getRequiresSafetyGuards = useCallback(() => {
    if (!patientShortId) return undefined;
    const data = apolloClient.readQuery<ClientRequiresTelehealthSafetyGuardsQuery>({
      query: GET_CLIENT_SAFETY_GUARD,
      variables: { patientShortId },
    });

    return data?.clientRequiresTelehealthSafetyGuards;
  }, [apolloClient, patientShortId]);

  return {
    loading,
    error,
    getRequiresSafetyGuards,
    requiresSafetyGuards: computeResult(data, loading, error),
    ...rest,
  };
}

const SAFETY_GUARD_RESOLUTION_TIMEOUT_MS = 3_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('client safety guard resolution timed out')), ms)),
  ]);
}

/**
 * Returns a callback that resolves whether the client requires telehealth safety
 * guards (true for clients under 13) and stores it in
 * `clientRequiresSafetyGuardsAtom`.
 *
 * Run during the pre-render gather phase, before the soft-block routing decision,
 * so the decision reads a settled value instead of racing an in-flight query.
 * Fails closed to `true` (requires guards) on timeout/error/missing data so an
 * under-13 client is never soft-blocked, while still sending the client straight
 * to the waiting room (care is never blocked). Also warms the Apollo cache for
 * the downstream `useGetClientSafetyGuard` consumers.
 */
export function useResolveClientSafetyGuardCallback() {
  const { patientShortId } = useParams();
  const setRequiresSafetyGuards = useSetAtom(clientRequiresSafetyGuardsAtom);
  const [fetchSafetyGuard] = useLazyQuery<ClientRequiresTelehealthSafetyGuardsQuery>(GET_CLIENT_SAFETY_GUARD, {
    context: { server: Server.MONOLITH, skipAuth: true },
  });

  return useCallback(async () => {
    try {
      if (!patientShortId) {
        setRequiresSafetyGuards(true);
        return;
      }
      const result = await withTimeout(
        fetchSafetyGuard({ variables: { patientShortId } }),
        SAFETY_GUARD_RESOLUTION_TIMEOUT_MS,
      );
      if (result.error) throw result.error;
      setRequiresSafetyGuards(result.data?.clientRequiresTelehealthSafetyGuards ?? true);
    } catch (error) {
      logger.error('Error resolving client safety guard', { patientShortId, error });
      setRequiresSafetyGuards(true);
    }
  }, [fetchSafetyGuard, patientShortId, setRequiresSafetyGuards]);
}
